import {
  Badge,
  Button,
  Dropdown,
  Icons,
  InputErrorMessage,
  NumberInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { hasZeroPercentOwner } from './compute';
import {
  INCOMPLETE_ENTITY_DETAILS_ERROR,
  hasIncompleteEntityDetails,
  zeroPercentOwnerError,
} from './copy';
import { type OwnershipEntry, ownerDisplayName } from './types';

interface MembershipTableProps {
  ids: string[];
  entries: Record<string, OwnershipEntry>;
  // When true, members carrying the Manager role get a badge (roles are set in the modal).
  managerManaged: boolean;
  // Σ of the entered percentages, and whether it equals 100 (M3).
  percentTotal: number;
  percentBalanced: boolean;
  onAddOwner: () => void;
  onEditOwner: (id: string) => void;
  onRemoveOwner: (id: string) => void;
  // Whether entity owners carry a details file; drives the incomplete badge.
  collectsEntityDetails?: boolean;
  onUpdatePercent: (id: string, percent: number | undefined) => void;
}

const PercentSuffix = () => (
  <span className="flex w-full items-center justify-center txt-small-regular text-text-tertiary">%</span>
);

export const MembershipTable = ({
  ids,
  entries,
  managerManaged,
  percentTotal,
  percentBalanced,
  onAddOwner,
  onEditOwner,
  onRemoveOwner,
  collectsEntityDetails,
  onUpdatePercent,
}: MembershipTableProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-bold text-text-primary">Membership</h3>
          <p className="text-sm text-text-secondary">Add members and define their ownership percentage.</p>
        </div>
        <Button variant="secondary" size="sm" type="button" onClick={onAddOwner}>
          + Add member
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead className="w-[240px]">% owned</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {ids.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-sm text-text-tertiary">
                No members added yet.
              </TableCell>
            </TableRow>
          ) : (
            ids.map((id) => {
              const entry = entries[id];
              if (!entry) return null;
              const needsDetails = !!collectsEntityDetails && entry.owner.kind === 'entity';
              return (
                <TableRow key={id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{ownerDisplayName(entry.owner)}</span>
                      <Badge variant="gray" size="sm">
                        {entry.owner.kind === 'individual' ? 'Individual' : 'Entity'}
                      </Badge>
                      {needsDetails && !entry.details ? (
                        <button type="button" onClick={() => onEditOwner(id)} className="cursor-pointer">
                          <Badge variant="warning" size="sm">
                            Details needed
                          </Badge>
                        </button>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <NumberInput
                      formatWithCommas
                      className="pr-9 text-right"
                      placeholder="0.00"
                      value={entry.ownershipPercent ?? undefined}
                      rightEl={PercentSuffix}
                      onChange={(n) => onUpdatePercent(id, n ?? undefined)}
                    />
                  </TableCell>
                  <TableCell>
                    <Dropdown
                      menu={{
                        groups: [
                          {
                            items: [
                              { label: 'Edit', onClick: () => onEditOwner(id) },
                              { label: 'Remove', onClick: () => onRemoveOwner(id) },
                            ],
                          },
                        ],
                      }}
                      align="end"
                    >
                      <div className="group flex size-8 cursor-pointer items-center justify-center">
                        <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
                      </div>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              );
            })
          )}
          <TableRow>
            <TableCell className="txt-small-medium text-text-primary">Total</TableCell>
            <TableCell>
              <NumberInput
                formatWithCommas
                className="pr-9 text-right"
                value={percentTotal}
                rightEl={PercentSuffix}
                disabled
                onChange={() => undefined}
              />
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>

      {ids.length > 0 && !percentBalanced ? <InputErrorMessage message="Ownership must total 100%." /> : null}
      {percentBalanced && hasZeroPercentOwner(ids, entries) ? (
        <InputErrorMessage message={zeroPercentOwnerError('membership')} />
      ) : null}
      {hasIncompleteEntityDetails(ids, entries, !!collectsEntityDetails) ? (
        <InputErrorMessage message={INCOMPLETE_ENTITY_DETAILS_ERROR} />
      ) : null}
      {ids.length > 0 && managerManaged && !ids.some((id) => entries[id]?.governanceRole === 'MANAGER') ? (
        <InputErrorMessage message="At least one member must be a Manager." />
      ) : null}
    </div>
  );
};
